import {
  DetailModel,
  FieldModel,
  PracticeTemplate,
  PracticeTemplateDetail,
  ProgramBenchmarkName,
  ProgramTask,
} from '@cibo/core'
import { useSaveDetail } from '@cibo/landmanager'
import { usePermissions } from '@cibo/profile'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import GridViewIcon from '@mui/icons-material/GridView'
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material'
import {
  GridCellParams,
  GridColumnHeaderParams,
  GridRenderCellParams,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { map, pipe, prop, uniq } from 'ramda'
import { KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoNeededCorner } from '../../../../components/TraitTable/InfoNeededCorner'
import { useProgramEngagementContext } from '../../../../context'
import { useProgramModel } from '../../../../queries'
import { PROGRAMS_QUERY_KEY } from '../../../../queries/queryKey'
import { DataEntrySchemaEditingProvider } from '../../Tasks/DataEntry/DataEntrySchemaEditingProvider'
import { ApplyTemplateWarning } from './ApplyTemplateWarning'
import { FieldTraitTaskEditorTableCell } from './FieldTraitTaskEditorTableCell'
import { FieldTraitTemplateEditorModal } from './TemplateEditorModal/FieldTraitTemplateEditorModal'
import { TemplateMenu } from './TemplateMenu/TemplateMenu'
import { TraitDataGrid } from './TraitDataGrid'
import { useFieldTraitColumns } from './useFieldTraitColumns'

type CopiedData =
  | { type: 'cell'; fieldId: string; traitId: string; detail: DetailModel }
  | { type: 'row'; fieldId: string; traits: Array<{ traitId: string; detail: DetailModel }> }

const removePastingKeys = (keys: string[]) => (prev: Set<string>) => {
  const next = new Set(prev)
  keys.forEach(k => next.delete(k))
  return next
}

export const FieldTraitTaskEditorTable = ({
  task,
}: {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName }>
}) => {
  const { engagement, fieldModels } = useProgramEngagementContext()
  const programModel = useProgramModel({ programId: engagement.data?.programId })
  const benchmark = programModel.data?.benchmarks?.find(
    ({ benchmark }) => benchmark === task.rule.params?.benchmarkName
  )
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation([
    '@cibo/landmanager/components',
    '@cibo/programs/TemplateEditorModal',
  ])
  const { isDebugUser } = usePermissions()

  const gridApiRef = useGridApiRef()
  const columnKeyHandlerRef = useRef<(e: globalThis.KeyboardEvent) => void>()

  const [copied, setCopied] = useState<CopiedData | null>(null)
  const [pastingCells, setPastingCells] = useState<Set<string>>(new Set())

  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<HTMLElement | null>(null)
  const [editorModalOpen, setEditorModalOpen] = useState(false)
  const [editorInitialTemplateId, setEditorInitialTemplateId] = useState<string | undefined>()
  const [applyWarningOpen, setApplyWarningOpen] = useState(false)
  const [pendingApply, setPendingApply] = useState<{
    template: PracticeTemplate
    fields: Array<{
      field: FieldModel
      applyableDetails: PracticeTemplateDetail[]
      skippedTraitIds: string[]
    }>
  } | null>(null)

  const queryClient = useQueryClient()
  const saveDetail = useSaveDetail()

  const invalidateEngagement = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, engagement.data?.id],
    })
    queryClient.invalidateQueries({ queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA] })
  }, [queryClient, engagement.data?.id])

  // Unique trait IDs from benchmark details
  const traitIds: string[] = pipe(map(prop('traitId')), uniq)(benchmark?.details ?? []) as string[]

  // One row per field
  const rows = (fieldModels.data?.items ?? []).map(fieldModel => ({
    id: fieldModel.id,
    fieldModel,
  }))

  const updateCellValue = (fieldId: string, traitId: string) => {
    const fieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
    gridApiRef.current?.updateRows([
      { id: fieldId, [traitId]: fieldModel?.findDetail({ traitId }) },
    ])
  }

  const renderTraitCell = useCallback(
    (traitId: string) => (params: GridRenderCellParams) =>
      (
        <FieldTraitTaskEditorTableCell
          {...params}
          row={params.row as { fieldModel: FieldModel; [key: string]: any }}
          value={params.row.fieldModel?.findDetail({ traitId }) as DetailModel | undefined}
          task={task}
          onSaveSuccess={() => {
            updateCellValue(params.row.fieldModel.id, traitId)
            invalidateEngagement()
          }}
          onDeleteSuccess={() => {
            updateCellValue(params.row.fieldModel.id, traitId)
            invalidateEngagement()
          }}
          isLoading={pastingCells.has(`${params.row.fieldModel.id}:${traitId}`)}
        />
      ),
    [task, pastingCells, invalidateEngagement, updateCellValue]
  )

  const columns = useFieldTraitColumns({ traitIds, renderTraitCell })

  const handleCellCopy = useCallback(
    (params: GridCellParams) => {
      const fieldId = String(params.id)
      if (params.field === 'field') {
        const fieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
        if (!fieldModel) return
        const traits = traitIds
          .map(t => ({ traitId: t, detail: fieldModel.findDetail({ traitId: t }) }))
          .filter((x): x is { traitId: string; detail: DetailModel } => x.detail != null)
        if (traits.length) setCopied({ type: 'row', fieldId, traits })
      } else {
        const fieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
        const detail = fieldModel?.findDetail({ traitId: params.field })
        if (
          detail &&
          (detail.input !== undefined ||
            detail.result !== undefined ||
            (detail as any).value !== undefined)
        )
          setCopied({ type: 'cell', fieldId, traitId: params.field, detail: detail as DetailModel })
      }
    },
    [fieldModels, traitIds]
  )

  const handleCellPaste = useCallback(
    (params: GridCellParams) => {
      if (!copied) return
      const fieldId = String(params.id)
      const targetFieldModel = fieldModels.data?.items?.find(f => f.id === fieldId)
      if (!targetFieldModel) return

      if (params.field === 'field') {
        if (copied.type !== 'row') return
        const details = copied.traits
          .filter(({ traitId: t }) => !targetFieldModel.findDetail({ traitId: t })?.immutable)
          .map(({ traitId: t, detail }) => ({
            traitId: t,
            ...(detail.input !== undefined ? { input: detail.input } : {}),
            ...(detail.result !== undefined ? { result: detail.result } : {}),
            ...((detail as any).value !== undefined ? { value: (detail as any).value } : {}),
          })) as any[]
        if (!details.length) return
        const keys = details.map((d: any) => `${fieldId}:${d.traitId}`)
        setPastingCells(prev => {
          const next = new Set(prev)
          keys.forEach((k: string) => next.add(k))
          return next
        })
        saveDetail
          .mutateAsync({ resourceId: targetFieldModel.resourceId, details })
          .then(() => {
            setPastingCells(removePastingKeys(keys))
            invalidateEngagement()
          })
          .catch((err: any) => {
            setPastingCells(removePastingKeys(keys))
            enqueueSnackbar(
              err?.response.data.message ||
                t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
              { variant: 'error' }
            )
          })
      } else {
        const traitId = params.field
        if (copied.type !== 'cell') return
        if (copied.traitId !== traitId) {
          enqueueSnackbar("Cannot paste — source and target traits don't match", {
            variant: 'error',
          })
          return
        }
        if (targetFieldModel.findDetail({ traitId })?.immutable) {
          enqueueSnackbar(t('cannotPasteLocked', { ns: '@cibo/programs/TaskEditorTable' }), {
            variant: 'error',
          })
          return
        }
        const key = `${fieldId}:${traitId}`
        setPastingCells(prev => new Set(prev).add(key))
        saveDetail
          .mutateAsync({
            resourceId: targetFieldModel.resourceId,
            details: [
              {
                traitId,
                ...(copied.detail.input !== undefined ? { input: copied.detail.input } : {}),
                ...(copied.detail.result !== undefined ? { result: copied.detail.result } : {}),
                ...((copied.detail as any).value !== undefined
                  ? { value: (copied.detail as any).value }
                  : {}),
              },
            ] as any[],
          })
          .then(() => {
            setPastingCells(removePastingKeys([key]))
            invalidateEngagement()
          })
          .catch((err: any) => {
            setPastingCells(removePastingKeys([key]))
            enqueueSnackbar(
              err?.response.data.message ||
                t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
              { variant: 'error' }
            )
          })
      }
    },
    [copied, fieldModels, saveDetail, enqueueSnackbar, invalidateEngagement]
  )

  const handleCellKeyDown = useCallback(
    (params: GridCellParams, event: MuiEvent<KeyboardEvent>) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        event.defaultMuiPrevented = true
        handleCellCopy(params)
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.defaultMuiPrevented = true
        handleCellPaste(params)
      } else if (event.key === 'Escape') {
        setCopied(null)
      }
    },
    [handleCellCopy, handleCellPaste]
  )

  const handleColumnHeaderClick = useCallback((_params: GridColumnHeaderParams) => {}, [])

  // Assigned on every render so the window listener always has a fresh closure
  columnKeyHandlerRef.current = (e: globalThis.KeyboardEvent) => {
    const headerEl = (document.activeElement as HTMLElement)?.closest?.(
      '[role="columnheader"][data-field]'
    )
    const traitId = headerEl?.getAttribute('data-field')
    if (!traitId || traitId === 'field') return

    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault()
      enqueueSnackbar(t('columnCopyNotSupported', { ns: '@cibo/programs/TaskEditorTable' }), {
        variant: 'warning',
      })
      return
    }

    if (e.key === 'Escape') setCopied(null)
  }

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => columnKeyHandlerRef.current?.(e)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const getCellClassName = useCallback(
    (params: GridCellParams) => {
      if (!copied || params.field === 'field') return ''
      const id = String(params.id)
      if (copied.type === 'cell') {
        return id === copied.fieldId && params.field === copied.traitId ? 'cell--copied' : ''
      }
      if (copied.type === 'row') {
        const isInCopied = copied.traits.some(t => t.traitId === params.field)
        if (!isInCopied) return ''
        return id === copied.fieldId ? 'cell--copied' : ''
      }
      return ''
    },
    [copied]
  )

  const handleOpenTemplateMenu = (event: MouseEvent<HTMLButtonElement>) =>
    setTemplateMenuAnchor(event.currentTarget)

  const handleSelectTemplate = (template: PracticeTemplate, fieldIds: string[]) => {
    const nonEmptyDetails = template.details.filter(
      d => d.input !== undefined || d.result !== undefined || d.value !== undefined
    )
    const fieldEntries = fieldIds.flatMap(fieldId => {
      const field = fieldModels.data?.items?.find(f => f.id === fieldId)
      if (!field) return []
      const skipped: string[] = []
      const applyableDetails = nonEmptyDetails.filter(d => {
        const existing = field.findDetail({ traitId: d.traitId })
        if (existing?.immutable) {
          if (!skipped.includes(d.traitId)) skipped.push(d.traitId)
          return false
        }
        return true
      })
      return [{ field, applyableDetails, skippedTraitIds: skipped }]
    })
    if (!fieldEntries.length) return
    const pending = { template, fields: fieldEntries }
    const anyFieldHasData = fieldEntries.some(({ field }) =>
      nonEmptyDetails.some(d => field.findDetail({ traitId: d.traitId }))
    )
    if (anyFieldHasData) {
      setPendingApply(pending)
      setApplyWarningOpen(true)
    } else {
      applyTemplate(pending)
    }
  }

  const applyTemplate = async (pending: NonNullable<typeof pendingApply>) => {
    const allSkipped = [...new Set(pending.fields.flatMap(f => f.skippedTraitIds))]
    try {
      for (const { field, applyableDetails } of pending.fields) {
        await saveDetail.mutateAsync({
          resourceId: field.resourceId,
          details: applyableDetails.map(d => ({
            traitId: d.traitId,
            ...(d.input !== undefined ? { input: d.input } : {}),
            ...(d.result !== undefined ? { result: d.result } : {}),
            ...(d.value !== undefined ? { value: d.value } : {}),
          })) as any[],
        })
      }
      invalidateEngagement()
      enqueueSnackbar(
        allSkipped.length > 0
          ? t('applyTemplatePartialSuccess', {
              ns: '@cibo/programs/TemplateEditorModal',
              count: allSkipped.length,
            })
          : t('applyTemplateSuccess', { ns: '@cibo/programs/TemplateEditorModal' }),
        { variant: allSkipped.length > 0 ? 'warning' : 'success' }
      )
    } catch {
      enqueueSnackbar(t('applyTemplateError', { ns: '@cibo/programs/TemplateEditorModal' }), {
        variant: 'error',
      })
    }
    setApplyWarningOpen(false)
    setPendingApply(null)
  }

  const handleConfirmApply = () => {
    if (pendingApply) applyTemplate(pendingApply)
  }

  const handleOpenNewTemplate = () => {
    setEditorInitialTemplateId(undefined)
    setEditorModalOpen(true)
  }

  const handleEditTemplate = (id: string) => {
    setEditorInitialTemplateId(id)
    setEditorModalOpen(true)
  }

  return (
    <DataEntrySchemaEditingProvider>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">
                {t('workflowData', {
                  ns: '@cibo/programs/TemplateEditorModal',
                })}
              </Typography>
              {isDebugUser && (
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <BugReportOutlinedIcon
                    fontSize="small"
                    color="error"
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleOpenTemplateMenu}
                    aria-label={t('openTemplateMenu', { ns: '@cibo/programs/TemplateEditorModal' })}
                    sx={{
                      borderColor: theme => theme.palette.error.main,
                      borderWidth: 1,
                      borderStyle: 'solid',
                    }}
                  >
                    <GridViewIcon />
                  </IconButton>
                </Box>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" pb={1} justifyContent="flex-end">
              <Box sx={{ width: 16, height: 16, position: 'relative' }}>
                <InfoNeededCorner />
              </Box>
              <Typography variant="caption" paddingLeft={0.5} sx={{ mr: '10px' }}>
                {t('infoNeeded', { ns: '@cibo/landmanager/components' })}
              </Typography>
            </Stack>
            <TraitDataGrid
              apiRef={gridApiRef}
              rows={rows}
              columns={columns}
              onCellKeyDown={handleCellKeyDown}
              getCellClassName={getCellClassName}
              onColumnHeaderClick={handleColumnHeaderClick}
            />
          </Stack>
        </Paper>
      </Stack>
      <TemplateMenu
        anchorEl={templateMenuAnchor}
        onClose={() => setTemplateMenuAnchor(null)}
        task={task as any}
        onSelectTemplate={handleSelectTemplate}
        onNewTemplate={handleOpenNewTemplate}
        onEditTemplate={handleEditTemplate}
        multiSelectFields
      />
      <FieldTraitTemplateEditorModal
        open={editorModalOpen}
        onClose={() => setEditorModalOpen(false)}
        task={task}
        initialTemplateId={editorInitialTemplateId}
      />
      <ApplyTemplateWarning
        open={applyWarningOpen}
        template={pendingApply?.template ?? null}
        onConfirm={handleConfirmApply}
        onCancel={() => {
          setApplyWarningOpen(false)
          setPendingApply(null)
        }}
        isApplying={saveDetail.isPending}
        immutableTraitIds={[...new Set(pendingApply?.fields.flatMap(f => f.skippedTraitIds) ?? [])]}
      />
    </DataEntrySchemaEditingProvider>
  )
}
